import { z } from 'zod';

import { shouldIncludeItem } from '@/lib/formHelpers';

export const contractVehicleFormSchema = z.object({
	vehicleId: z.string().optional(),
	plateNumber: z.string().optional(), // For display only - not sent in mutation
	isDeleted: z.boolean().optional().default(false)
});

export const contractVipVehicleFormSchema = z.object({
	vehicleId: z.string().optional(),
	plateNumber: z.string().optional(), // For display only
	isDeleted: z.boolean().optional().default(false)
});

export const contractCardFormSchema = z.object({
	contractCardId: z.string().optional(),
	facilityId: z.string().optional(),
	cardType: z.enum(['RFID', 'BARCODE', 'MAGNETIC']).default('RFID'),
	cardKey: z.string().optional(),
	cardStatusId: z.enum(['ACTIVE', 'SUSPENDED', 'RETURNED', 'LOST', 'REPLACED', 'DEACTIVATED', 'EXPIRED']).default('ACTIVE'),
	isDeleted: z.boolean().optional().default(false)
});

export const contractFormSchema = z
	.object({
		customerId: z
			.string({ required_error: 'Select a customer.' })
			.min(1, 'Select a customer.'),
		beginDate: z
			.string({ required_error: 'Begin date is required.' })
			.min(1, 'Begin date is required.')
			.refine(value => !Number.isNaN(new Date(value).getTime()), {
				message: 'Begin date must be a valid date.'
			}),
		endDate: z
			.string({ required_error: 'End date is required.' })
			.min(1, 'End date is required.')
			.refine(value => !Number.isNaN(new Date(value).getTime()), {
				message: 'End date must be a valid date.'
			}),
		amount: z
			.string({ required_error: 'Monthly amount is required.' })
			.trim()
			.refine(value => {
				const numeric = Number(value);
				return !Number.isNaN(numeric) && numeric > 0;
			}, 'Monthly amount must be a positive number.'),
		isAutoRenewed: z.boolean(),
		simultaneousVehicleLimit: z
			.string()
			.optional()
			.transform(value => (value ?? '').trim()),
		reservedParkingSlots: z
			.string()
			.optional()
			.transform(value => (value ?? '').trim())
			.refine(value => {
				if (!value) return true;
				const numeric = Number(value);
				return !Number.isNaN(numeric) && numeric >= 0;
			}, 'Reserved parking slots must be 0 or greater.'),
		isActive: z.boolean(),
		preferredLanguage: z.enum(['EN', 'EL', '']).default(''),
		includeAllCustomerVehicles: z.boolean().default(true),
		vehicles: z.array(contractVehicleFormSchema).optional().default([]),
		vipVehicles: z.array(contractVipVehicleFormSchema).optional().default([]),
		cards: z.array(contractCardFormSchema).optional().default([]),
		notes: z.array(z.string()).optional()
	})
	.superRefine((values, ctx) => {
		const begin = new Date(values.beginDate);
		const end = new Date(values.endDate);

		if (begin.getTime() > end.getTime()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['endDate'],
				message: 'End date must be on or after the begin date.'
			});
		}
	});

export type ContractVehicleFormValues = z.infer<
	typeof contractVehicleFormSchema
>;
export type ContractVipVehicleFormValues = z.infer<
	typeof contractVipVehicleFormSchema
>;
export type ContractCardFormValues = z.infer<typeof contractCardFormSchema>;
export type ContractFormValues = z.infer<typeof contractFormSchema>;

export interface ContractCardSubmitData {
	facilityId: string;
	cardType: 'RFID' | 'BARCODE' | 'MAGNETIC';
	cardKey: string;
	cardStatusId: string;
}

export interface ContractFormSubmitData {
	customerId: string;
	beginDate: string;
	endDate: string;
	amount: number;
	isAutoRenewed: boolean;
	simultaneousVehicleLimit?: number | null;
	reservedParkingSlots?: number | null;
	isActive: boolean;
	preferredLanguage: 'EN' | 'EL' | '' | null;
	includeAllCustomerVehicles: boolean;
	vehicleIds: string[];
	vipVehicleIds: string[];
	cards: ContractCardSubmitData[];
}

export const defaultContractFormValues: ContractFormValues = {
	customerId: '',
	beginDate: '',
	endDate: '',
	amount: '',
	isAutoRenewed: true,
	simultaneousVehicleLimit: '',
	reservedParkingSlots: '',
	isActive: true,
	preferredLanguage: '',
	includeAllCustomerVehicles: true,
	vehicles: [],
	vipVehicles: [],
	cards: []
};

export const defaultContractCardItem: ContractCardFormValues = {
	contractCardId: undefined,
	facilityId: undefined,
	cardType: 'RFID',
	cardKey: '',
	cardStatusId: 'ACTIVE',
	isDeleted: false
};

export const buildContractFormSubmitData = (
	values: ContractFormValues,
	currentFacilityId?: string | null
): ContractFormSubmitData => {
	const parseNumeric = (input: string | undefined | null) => {
		if (!input) {
			return null;
		}

		const numeric = Number(input);
		return Number.isNaN(numeric) ? null : numeric;
	};

	// Explicit vehicles (only relevant when includeAllCustomerVehicles = false)
	const vehicleIds = values.includeAllCustomerVehicles
		? []
		: (values.vehicles ?? [])
				.filter(v => !v.isDeleted)
				.filter(v => shouldIncludeItem(v, 'vehicleId'))
				.map(v => v.vehicleId!)
				.filter(Boolean);

	// VIP vehicle IDs (always relevant, independent of includeAllCustomerVehicles)
	const vipVehicleIds = (values.vipVehicles ?? [])
		.filter(v => !v.isDeleted)
		.filter(v => shouldIncludeItem(v, 'vehicleId'))
		.map(v => v.vehicleId!)
		.filter(Boolean);

	// Contract cards
	const cards: ContractCardSubmitData[] = (values.cards ?? [])
		.filter(c => !c.isDeleted)
		.filter(c => (c.cardKey ?? '').trim().length > 0)
		.map(c => ({
			facilityId: c.facilityId || currentFacilityId || '',
			cardType: c.cardType,
			cardKey: c.cardKey!.trim(),
			cardStatusId: c.cardStatusId
		}))
		.filter(c => c.facilityId.length > 0);

	return {
		customerId: values.customerId,
		beginDate: values.beginDate,
		endDate: values.endDate,
		amount: Number(values.amount),
		isAutoRenewed: values.isAutoRenewed,
		simultaneousVehicleLimit: parseNumeric(values.simultaneousVehicleLimit),
		reservedParkingSlots: parseNumeric(values.reservedParkingSlots),
		isActive: values.isActive,
		preferredLanguage: values.preferredLanguage || null,
		includeAllCustomerVehicles: values.includeAllCustomerVehicles,
		vehicleIds,
		vipVehicleIds,
		cards
	};
};
